import { LeadStatus } from '@bariendo/lib/active-lead';
import { useMedplum } from '@medplum/react';
import { useCallback, useState } from 'react';
import { useUndoableChanges } from '@/hooks/useUndoableChanges';
import { alert } from '@/services/alert';
import { reportError } from '@/services/error-reporting';
import { fetchBot } from '@/services/services';

/**
 * Lead status changes from a hub table (BAR-2801). Active saves at once; Hold
 * and Lost wait five seconds for an Undo, and the bot is only called when that
 * runs out. A saved status shows on its row straight away, and a row whose new
 * status no longer matches the Status filter fades out before it leaves.
 */

const LEAVE_MS = 180;

export interface UseLeadStatusChangesOptions {
  /** Whether a row with this status stays in the list under the current filter. */
  matchesFilter: (status: LeadStatus) => boolean;
}

export function useLeadStatusChanges({ matchesFilter }: UseLeadStatusChangesOptions) {
  const medplum = useMedplum();
  // Statuses saved from the table this session, by patient id.
  const [saved, setSaved] = useState<Map<string, LeadStatus>>(new Map());
  const [leaving, setLeaving] = useState<Set<string>>(new Set());

  const setSavedStatus = useCallback((patientId: string, status: LeadStatus | undefined) => {
    setSaved((prev) => {
      const next = new Map(prev);
      if (status) {
        next.set(patientId, status);
      } else {
        next.delete(patientId);
      }
      return next;
    });
  }, []);

  const commit = useCallback(
    (patientId: string, status: LeadStatus) => {
      const show = (): void => setSavedStatus(patientId, status);

      if (matchesFilter(status)) {
        show();
      } else {
        setLeaving((prev) => new Set(prev).add(patientId));
        setTimeout(() => {
          show();
          setLeaving((prev) => {
            const next = new Set(prev);
            next.delete(patientId);
            return next;
          });
        }, LEAVE_MS);
      }

      fetchBot(medplum, 'set-lead-status', { patientId, status }).catch((error: unknown) => {
        reportError(error, { component: 'useLeadStatusChanges' });
        alert.error("Couldn't update the lead status. Please try again.");
        setSavedStatus(patientId, undefined);
      });
    },
    [medplum, matchesFilter, setSavedStatus]
  );

  const { pending, schedule, undo } = useUndoableChanges<string, LeadStatus>({ onCommit: commit });

  const change = useCallback(
    (patientId: string, status: LeadStatus) => {
      if (status === 'active') {
        undo(patientId);
        commit(patientId, status);
      } else {
        schedule(patientId, status);
      }
    },
    [commit, schedule, undo]
  );

  return { saved, pending, leaving, change, undo };
}
